<template>
  <div class="agreement-sign-page" :dir="isHebrew ? 'rtl' : 'ltr'">

    <!-- Not logged in -->
    <div v-if="state === 'not_logged_in'" class="agreement-sign-center">
      <b-icon icon="person-x-fill" font-scale="3" class="text-secondary" />
      <p>{{ t('agreementSignPageNotLoggedIn') }}</p>
    </div>

    <!-- Loading -->
    <div v-else-if="state === 'loading'" class="agreement-sign-center">
      <div class="spinner-border text-secondary" role="status" />
      <p class="agreement-sign-hint">{{ t('agreementSignPageLoading') }}</p>
    </div>

    <!-- Already signed -->
    <div v-else-if="state === 'already_signed'" class="agreement-sign-center agreement-sign-status--success">
      <b-icon icon="check-circle-fill" font-scale="3" />
      <p>{{ t('agreementSignPageAlreadySigned') }}</p>
    </div>

    <!-- Success -->
    <div v-else-if="state === 'success'" class="agreement-sign-center agreement-sign-status--success">
      <b-icon icon="check-circle-fill" font-scale="3" />
      <h2>{{ t('agreementSignPageSuccessTitle') }}</h2>
      <p>{{ t('agreementSignPageSuccessBody') }}</p>
    </div>

    <!-- Agreement form -->
    <div v-else-if="state === 'form'" class="agreement-sign-shell">

      <header class="contract-hero">
        <div class="contract-hero-accent" aria-hidden="true" />
        <div class="contract-hero-inner">
          <p class="contract-hero-eyebrow">{{ t('businessBasicAgreementHeroEyebrow') }}</p>
          <h1 class="contract-hero-title">{{ t('businessBasicAgreementModalTitle') }}</h1>
          <p class="contract-hero-meta">
            <span class="contract-hero-version">{{ t('businessBasicAgreementVersionLabel') }}</span>
            <span class="contract-hero-dot" aria-hidden="true">·</span>
            <span class="contract-hero-hint">{{ t('businessBasicAgreementHeroHint') }}</span>
          </p>
        </div>
      </header>

      <!-- Agreement text -->
      <div class="agreement-sign-body" :dir="isHebrew ? 'rtl' : 'ltr'">
        <div class="contract-paper">
          <div class="contract-paper-inner-border">
            <article class="contract-prose">
              <h3 class="contract-doc-title">{{ agreementDoc.docTitle }}</h3>
              <p class="contract-intro">{{ agreementDoc.intro }}</p>
              <section
                v-for="(section, si) in agreementDoc.sections"
                :key="'sec-' + si"
                class="contract-section"
              >
                <h4 class="contract-section-title">{{ section.title }}</h4>
                <template v-for="(block, bi) in section.blocks">
                  <p v-if="block.type === 'p'" :key="'p-' + si + '-' + bi" class="contract-p">{{ block.text }}</p>
                  <ul v-else-if="block.type === 'ul'" :key="'ul-' + si + '-' + bi" class="contract-list">
                    <li v-for="(item, ii) in block.items" :key="ii" class="contract-list-item">{{ item }}</li>
                  </ul>
                  <div v-else-if="block.type === 'table'" :key="'tb-' + si + '-' + bi" class="contract-table-wrap" dir="auto">
                    <table class="contract-table">
                      <thead>
                        <tr><th v-for="(h, hi) in block.headers" :key="hi">{{ h }}</th></tr>
                      </thead>
                      <tbody>
                        <tr v-for="(row, ri) in block.rows" :key="ri">
                          <td v-for="(cell, ci) in row" :key="ci">{{ cell }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-else-if="block.type === 'stack'" :key="'st-' + si + '-' + bi" class="contract-subsection">
                    <h5 class="contract-subsection-title">{{ block.title }}</h5>
                    <template v-for="(b2, bj) in block.blocks">
                      <p v-if="b2.type === 'p'" :key="'sb-p-' + si + '-' + bi + '-' + bj" class="contract-p">{{ b2.text }}</p>
                      <ul v-else-if="b2.type === 'ul'" :key="'sb-ul-' + si + '-' + bi + '-' + bj" class="contract-list contract-list--nested">
                        <li v-for="(item, ii) in b2.items" :key="ii" class="contract-list-item">{{ item }}</li>
                      </ul>
                    </template>
                  </div>
                </template>
              </section>
            </article>
          </div>
        </div>
      </div>

      <!-- Phone -->
      <div ref="phoneSection" class="contract-phone-wrap" :dir="isHebrew ? 'rtl' : 'ltr'">
        <label class="contract-phone-label" for="sign-page-phone">{{ t('businessBasicAgreementPhoneLabel') }}</label>
        <input
          id="sign-page-phone"
          ref="phoneInput"
          v-model="phone"
          type="tel"
          inputmode="tel"
          class="contract-phone-input"
          :disabled="isSubmitting"
          autocomplete="tel"
          :placeholder="t('businessBasicAgreementPhonePlaceholder')"
          @blur="phoneBlurred = true"
        />
        <p v-if="phoneHintVisible" class="contract-phone-hint">{{ t('businessBasicAgreementPhoneHint') }}</p>
        <p v-if="phoneErrorText" class="contract-phone-error" role="alert">{{ phoneErrorText }}</p>
      </div>

      <!-- Checkbox -->
      <div ref="ackSection" class="contract-ack" :dir="isHebrew ? 'rtl' : 'ltr'">
        <h4 class="contract-ack-title">{{ t('businessBasicAgreementAckTitle') }}</h4>
        <label class="contract-ack-label">
          <input v-model="accepted" type="checkbox" class="contract-ack-input" :disabled="isSubmitting" />
          <span class="contract-ack-copy">{{ t('businessBasicAgreementCheckbox') }}</span>
        </label>
        <label class="contract-ack-label">
          <input v-model="acceptedAi" type="checkbox" class="contract-ack-input" :disabled="isSubmitting" />
          <span class="contract-ack-copy">{{ t('businessBasicAgreementAiCheckbox') }}</span>
        </label>
        <p v-if="ackErrorText" class="contract-ack-error" role="alert">{{ ackErrorText }}</p>
      </div>

      <!-- Footer -->
      <footer class="contract-footer" :dir="isHebrew ? 'rtl' : 'ltr'">
        <p v-if="submitErrorText" class="agreement-sign-submit-error" role="alert">{{ submitErrorText }}</p>
        <button
          type="button"
          class="contract-btn contract-btn-primary"
          :disabled="isSubmitting"
          @click="onSubmit"
        >
          <span v-if="isSubmitting" class="contract-btn-spinner spinner-border spinner-border-sm" role="status" aria-hidden="true" />
          {{ t('businessBasicAgreementAccept') }}
        </button>
      </footer>

    </div>
  </div>
</template>

<script>
import { translationMixin } from '@/mixins/translationMixin';
import { getBusinessBasicAgreementByRegion } from '@/content/saasBusinessBasicAgreementText';
import { isValidContactPhone } from '@/common/validateContactPhone';
import { recordUserAgreement, BUSINESS_BASIC_AGREEMENT_TYPE, BUSINESS_BASIC_AGREEMENT_VERSION } from '@/api/agreements';
import billingApi from '@/api/billing';
import { mapGetters } from 'vuex';

export default {
  name: 'AgreementSignPage',
  mixins: [translationMixin],
  data() {
    return {
      state: 'loading',   // loading | not_logged_in | already_signed | form | success
      phone: '',
      accepted: false,
      acceptedAi: false,
      phoneBlurred: false,
      ackSubmitAttempted: false,
      phoneSubmitAttempted: false,
      isSubmitting: false,
      submitErrorText: '',
    };
  },
  computed: {
    ...mapGetters(['apptoken', 'isAppLoggedIn']),
    agreementDoc() {
      return getBusinessBasicAgreementByRegion(this.$store.state.regionConfig.currency);
    },
    phoneValid() {
      return isValidContactPhone(this.phone);
    },
    phoneHintVisible() {
      return !this.phoneErrorText;
    },
    phoneErrorText() {
      if (!this.phoneBlurred && !this.phoneSubmitAttempted) return '';
      const p = (this.phone || '').trim();
      if (!p) return this.t('businessBasicAgreementPhoneRequired');
      if (!isValidContactPhone(p)) return this.t('businessBasicAgreementPhoneInvalid');
      return '';
    },
    allAcksChecked() {
      return this.accepted && this.acceptedAi;
    },
    ackErrorText() {
      if (!this.ackSubmitAttempted || this.allAcksChecked) return '';
      return this.t('businessBasicAgreementCheckboxRequired');
    },
  },
  async created() {
    if (!this.isAppLoggedIn) {
      this.state = 'not_logged_in';
      return;
    }
    try {
      const res = await billingApi.getBillingProfile(this.apptoken);
      if (res.data?.business_basic_agreement_latest_accepted === true) {
        this.state = 'already_signed';
      } else {
        this.state = 'form';
      }
    } catch {
      // If profile fetch fails, still show the form
      this.state = 'form';
    }
  },
  methods: {
    scrollTo(refName) {
      const el = this.$refs[refName];
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    async onSubmit() {
      if (this.isSubmitting) return;
      this.ackSubmitAttempted = true;
      this.phoneSubmitAttempted = true;
      this.submitErrorText = '';

      if (!this.allAcksChecked) {
        this.scrollTo('ackSection');
        return;
      }
      if (!this.phoneValid) {
        this.phoneBlurred = true;
        this.scrollTo('phoneSection');
        this.$nextTick(() => this.$refs.phoneInput && this.$refs.phoneInput.focus());
        return;
      }

      this.isSubmitting = true;
      try {
        const lang = this.isHebrew ? 'he' : 'en';
        await recordUserAgreement(this.apptoken, {
          agreement_type: BUSINESS_BASIC_AGREEMENT_TYPE,
          agreement_version: BUSINESS_BASIC_AGREEMENT_VERSION,
          language: lang,
          source: 'update_terms',
          phone: this.phone.trim(),
          user_agent: navigator.userAgent || '',
        });
        this.state = 'success';
      } catch {
        this.submitErrorText = this.t('agreementSignPageErrorBody');
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.agreement-sign-page {
  min-height: 100vh;
  background: #f4f1eb;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.agreement-sign-shell {
  width: 100%;
  max-width: 860px;
  margin: 3.2rem auto;
  padding-bottom: 11rem;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 12px 48px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
  background: #f4f1eb;
}
.agreement-sign-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6.4rem 3.2rem;
  gap: 1.6rem;
  text-align: center;
  font-size: 1.76rem;
}
.agreement-sign-status--success { color: #0f766e; }

/* Hero */
.contract-hero {
  position: relative;
  padding: 2.8rem 2.8rem 2.16rem;
  background: linear-gradient(165deg, #141c2b 0%, #0a0f18 48%, #111827 100%);
  color: #f8fafc;
  text-align: center;
  overflow: hidden;
}
.contract-hero-accent {
  position: absolute; top: 0; left: 0; right: 0; height: 4px;
  background: linear-gradient(90deg, #8b7355, #d4af6e, #c9a227, #d4af6e, #8b7355);
}
.contract-hero-inner { position: relative; z-index: 1; }
.contract-hero-eyebrow {
  font-size: 1.15rem; letter-spacing: 0.28em; text-transform: uppercase;
  color: rgba(212, 175, 106, 0.95); margin-bottom: 1.04rem; font-weight: 600;
}
.contract-hero-title {
  font-family: 'Frank Ruhl Libre', 'Times New Roman', Georgia, serif;
  font-size: clamp(2.16rem, 4.5vw, 2.96rem); font-weight: 700; line-height: 1.35;
  margin: 0 0 1.2rem; color: #fff;
}
.contract-hero-meta {
  margin: 0; font-size: 1.41rem; color: rgba(226, 232, 240, 0.88);
  display: flex; flex-wrap: wrap; align-items: center; justify-content: center; gap: 0.56rem 0.8rem;
}
.contract-hero-version { font-weight: 600; color: #e2c77a; }
.contract-hero-dot     { opacity: 0.55; }
.contract-hero-hint    { opacity: 0.9; font-size: 1.31rem; }

/* Agreement body */
.agreement-sign-body {
  padding: 2rem;
  background: linear-gradient(180deg, #e8e4dc 0%, #d4cfc4 100%);
}
.contract-paper {
  padding: 2px;
  background: linear-gradient(145deg, #c9a22733, #1e293b22);
  border-radius: 4px;
}
.contract-paper-inner-border {
  background: #fdfbf7; border: 1px solid rgba(139, 115, 85, 0.35);
  border-radius: 2px; padding: 2.16rem 2.4rem 2.4rem;
}
.contract-prose {
  font-family: 'Frank Ruhl Libre', 'David Libre', 'Times New Roman', Georgia, serif;
  font-size: 1.63rem; line-height: 1.7; color: #1a1a1a;
  text-align: start;
}
.contract-prose[dir='ltr'],
[dir='ltr'] .contract-prose { text-align: left; }
.contract-prose[dir='rtl'],
[dir='rtl'] .contract-prose { text-align: right; }
.contract-doc-title {
  font-size: 2.11rem; font-weight: 700; color: #0f172a; margin: 0 0 1.36rem;
  padding-bottom: 1.2rem; border-bottom: 2px solid rgba(201, 162, 39, 0.5);
}
.contract-intro { margin: 0 0 2.16rem; color: #334155; }
.contract-section {
  margin: 0; padding: 1.76rem 0 1.92rem; border-top: 1px solid rgba(139, 115, 85, 0.22);
}
.contract-section:first-of-type { border-top: none; }
.contract-section-title {
  font-size: 1.76rem; font-weight: 700; color: #0f172a; margin: 0 0 1.12rem;
  padding: 0.64rem 1.2rem; background: rgba(201, 162, 39, 0.11);
  border-inline-start: 4px solid #a67c00; border-radius: 4px;
}
.contract-subsection {
  margin: 1.2rem 0 1.52rem; padding: 1.04rem 1.36rem;
  background: rgba(255, 255, 255, 0.72); border-radius: 6px;
  border: 1px solid rgba(139, 115, 85, 0.18);
}
.contract-subsection-title { font-size: 1.52rem; font-weight: 600; color: #475569; margin: 0 0 0.72rem; }
.contract-p          { margin: 0 0 1.04rem; }
.contract-list       { margin: 0 0 1.2rem; padding-inline-start: 2rem; }
.contract-list-item  { margin-bottom: 0.51rem; }

.contract-table-wrap { overflow-x: auto; margin: 0 0 1.2rem; }
.contract-table { width: 100%; border-collapse: collapse; font-size: 0.92em; }
.contract-table th,
.contract-table td {
  border: 1px solid rgba(139, 115, 85, 0.3); padding: 0.4em 0.6em;
  text-align: center; vertical-align: middle;
}
.contract-table th { background: rgba(201, 162, 39, 0.11); font-weight: 700; color: #0f172a; }
.contract-table th:first-child,
.contract-table td:first-child { text-align: start; font-weight: 600; }

/* Phone */
.contract-phone-wrap {
  padding: 1.36rem 2.4rem 0.8rem;
  background: #f4f1eb;
  border-top: 1px solid rgba(139, 115, 85, 0.2);
  text-align: start;
}
.contract-phone-wrap[dir='ltr'] { text-align: left; }
.contract-phone-wrap[dir='rtl'] { text-align: right; }
.contract-phone-label { display: block; font-size: 1.52rem; font-weight: 600; color: #1e293b; margin-bottom: 0.64rem; }
.contract-phone-input {
  width: 100%; max-width: 35rem; padding: 0.88rem 1.2rem; font-size: 1.68rem;
  border: 1px solid rgba(139, 115, 85, 0.35); border-radius: 6px;
  background: #fff; color: #0f172a; direction: ltr; text-align: left;
}
.contract-phone-hint  { margin: 0.56rem 0 0; font-size: 1.31rem; color: #64748b; }
.contract-phone-error { margin: 0.56rem 0 0; font-size: 1.41rem; color: #b91c1c; }

/* Checkbox */
.contract-ack {
  padding: 1.6rem 2.4rem 0.4rem; background: #f4f1eb;
  border-top: 1px solid rgba(139, 115, 85, 0.2);
  text-align: start;
}
.contract-ack[dir='ltr'] { text-align: left; }
.contract-ack[dir='rtl'] { text-align: right; }
.contract-ack-title { text-align: start; margin: 0 0 0.96rem; font-size: 1.76rem; font-weight: 700; color: #0f172a; }
.contract-ack-label + .contract-ack-label { margin-top: 0.88rem; }
.contract-ack-label { display: flex; align-items: flex-start; gap: 1.36rem; cursor: pointer; font-size: 1.68rem; font-weight: 600; color: #1e293b; }
.contract-ack-input { margin-top: 0.56rem; width: 1.84rem; height: 1.84rem; flex-shrink: 0; accent-color: #0f766e; }
.contract-ack-error { margin: 0.72rem 0 0; font-size: 1.41rem; color: #b91c1c; }

/* Footer - always visible, styled to match the bottom of the form card */
.contract-footer {
  padding: 1.84rem 2.4rem 2.16rem;
  background: linear-gradient(180deg, #f8f6f2 0%, #f0ebe3 100%);
  border-top: 1px solid rgba(139, 115, 85, 0.25);
  display: flex; flex-direction: column; align-items: flex-end; gap: 1.2rem;

  /* Pinned to viewport bottom, sized like the form card (same max-width, centered). */
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 860px;
  z-index: 100;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
  box-shadow: 0 -8px 18px rgba(15, 23, 42, 0.14);
}
.agreement-sign-submit-error { font-size: 1.47rem; color: #b91c1c; margin: 0; }

/* Button */
.contract-btn {
  font-family: system-ui, sans-serif; font-weight: 600; font-size: 1.6rem;
  padding: 1.04rem 3.2rem; border-radius: 6px; border: none; cursor: pointer;
  min-width: 16rem; transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.contract-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.contract-btn-primary {
  background: linear-gradient(180deg, #0d9488 0%, #0f766e 45%, #0d5c54 100%);
  color: #fff;
  box-shadow: 0 2px 4px rgba(15, 118, 110, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.15);
}
.contract-btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(15, 118, 110, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2);
}
.contract-btn-spinner { vertical-align: -0.15em; margin-inline-end: 0.64rem; }

@media (max-width: 600px) {
  .agreement-sign-shell { margin: 0; border-radius: 0; padding-bottom: 13rem; }
  .contract-btn { width: 100%; }
  .contract-footer {
    max-width: none;
    left: 0; right: 0;
    transform: none;
    border-radius: 0;
  }
}
</style>
